import React, { useRef } from 'react';
import { Transition } from 'react-transition-group';
import { GridRow } from '@naf/grid';
import { ARTICLE_PAGE_SIZE } from '@redux/modules/articles';
import { ArticleType } from '../../../../types/articleType';
import { ArticleLoader } from '../skeleton-loaders/ArticleLoader';
import { AnimationWrapper } from '../skeleton-loaders/AnimationWrapper';
import FinderIllustration from '../icons/DynamicallyImportedFinderIllustration';
import { getPlaceholderList } from '../../utils/getPlaceholderList';
import { ArticleItemCard } from './components/ArticleItemCard';
import { ArticleGridMessage } from './components/ArticleGridMessage';
import { CardGridWrapper, TransitionGrid } from './ArticleGrid.styles';

interface Props {
  articleItems: Record<string, ArticleType>;
  error?: boolean;
  updating?: boolean;
  showDate?: boolean;
  showNewsLabel?: boolean;
  filterByAuthor?: boolean;
  /** First page of a list — only its first card can be the LCP element. */
  isLeadPage?: boolean;
}

const ArticleGrid = ({
  articleItems,
  error,
  updating,
  showDate = false,
  showNewsLabel = false,
  filterByAuthor = false,
  isLeadPage = false,
}: Props) => {
  const articles = articleItems && Object.values(articleItems);
  const placeholderGridRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const articlesCount = articleItems ? Object.values(articleItems).length : null;

  return (
    <>
      <TransitionGrid>
        {updating && (
          <Transition
            nodeRef={placeholderGridRef}
            in={updating}
            timeout={{ enter: 250, exit: 0 }}
            unmountOnExit
            mountOnEnter
          >
            {(state) => (
              <AnimationWrapper ref={placeholderGridRef} $state={state}>
                <GridRow>
                  {getPlaceholderList(ARTICLE_PAGE_SIZE).map((i) => (
                    <CardGridWrapper s="12" m="12" l="4" xl="4" key={`article-placeholder-item-${i}`}>
                      <ArticleLoader />
                    </CardGridWrapper>
                  ))}
                </GridRow>
              </AnimationWrapper>
            )}
          </Transition>
        )}
        {!updating && !error && articlesCount > 0 && (
          <Transition nodeRef={gridRef} key="data" in={!updating} timeout={250} unmountOnExit mountOnEnter>
            {(state) => (
              <AnimationWrapper ref={gridRef} $state={state}>
                {Object.keys(articles).length > 0 && (
                  <GridRow>
                    {articles.map((articleItem, index) => (
                      <ArticleItemCard
                        article={articleItem}
                        showDate={showDate}
                        showNewsLabel={showNewsLabel}
                        isLcpCandidate={isLeadPage && index === 0}
                        key={`s-${articleItem.slug}`}
                      />
                    ))}
                  </GridRow>
                )}
              </AnimationWrapper>
            )}
          </Transition>
        )}
      </TransitionGrid>
      {!updating && error ? (
        <ArticleGridMessage title="Det har dessverre oppstått en feil.">
          Vennligst prøv igjen senere, eller kontakt kundeservice dersom problemet vedvarer
        </ArticleGridMessage>
      ) : null}
      {!updating && !error && articles && articlesCount <= 0 ? (
        <ArticleGridMessage title="Her var det tomt!" illustration={<FinderIllustration />}>
          Vi kan ikke finne noen artikler {filterByAuthor ? 'fra valgt forfatter.' : 'med valgt kategori.'}
        </ArticleGridMessage>
      ) : null}
    </>
  );
};

export default ArticleGrid;
